import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Webcam from "react-webcam";

import {
  MdArrowBack,
  MdCheckCircleOutline,
  MdClose,
  MdCloudUpload,
  MdErrorOutline,
  // Status & Info
  MdInfoOutline,
  MdKeyboard,
  // Navigation
  MdKeyboardArrowRight,
  MdLockOutline,
  MdMic,
  MdMicOff,
  MdMouse,
  MdPersonOutline,
  // Actions
  MdPlayArrow,
  // Loading & Progress
  MdRefresh,
  MdScreenShare,
  MdStop,
  // Video & Audio
  MdVideocam,
  MdVideocamOff,
  MdVisibility,
  MdVolumeUp,
  MdWarningAmber,
} from "react-icons/md";

import { MdCameraAlt, MdTimer } from "react-icons/md";

import styles from "./EnhancedInterviewSystem.module.css";

const SAMPLE_RATE = 48000;
const CHUNK_MS = 100;
const AUDIO_THRESHOLD = 0.003;
const SILENCE_DURATION = 800;

// Convert float32 audio to 16-bit PCM for WebSocket transmission [web:66]
function floatTo16BitPCM(float32Array) {
  const length = float32Array.length;
  const buffer = new ArrayBuffer(length * 2);
  const view = new DataView(buffer);
  for (let i = 0; i < length; i++) {
    const sample = Math.max(-1, Math.min(1, float32Array[i]));
    view.setInt16(i * 2, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true);
  }
  return buffer;
}

// Audio player class for handling received audio from WebSocket [web:66][web:74]
class AudioPlayer {
  constructor() {
    this.audioContext = null;
    this.queue = [];
    this.nextTime = 0;
    this.isPlaying = false;
    this.gainNode = null;
  }

  async init() {
    if (!this.audioContext) {
      this.audioContext = new (window.AudioContext ||
        window.webkitAudioContext)();
      this.gainNode = this.audioContext.createGain();
      this.gainNode.connect(this.audioContext.destination);

      if (this.audioContext.state === "suspended") {
        await this.audioContext.resume();
      }
      console.log("🎵 Audio player initialized");
    }
  }

  async playAudio(arrayBuffer) {
    await this.init();

    try {
      const int16Array = new Int16Array(arrayBuffer);
      const float32Array = new Float32Array(int16Array.length);

      for (let i = 0; i < int16Array.length; i++) {
        float32Array[i] = int16Array[i] / 32768;
      }

      this.queue.push(float32Array);
      this.schedule();
    } catch (error) {
      console.error("❌ Audio playback error:", error);
    }
  }

  schedule() {
    if (!this.audioContext || !this.gainNode) return;

    if (!this.isPlaying) {
      this.nextTime = Math.max(
        this.audioContext.currentTime,
        this.nextTime || 0
      );
      this.isPlaying = true;
    }

    while (this.queue.length > 0) {
      const chunk = this.queue.shift();
      const audioBuffer = this.audioContext.createBuffer(
        1,
        chunk.length,
        this.audioContext.sampleRate
      );
      audioBuffer.getChannelData(0).set(chunk);

      const source = this.audioContext.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(this.gainNode);
      source.start(this.nextTime);

      this.nextTime += audioBuffer.length / this.audioContext.sampleRate;
    }

    if (this.queue.length === 0) {
      this.isPlaying = false;
    }
  }

  setVolume(volume) {
    if (this.gainNode) {
      this.gainNode.gain.value = volume;
    }
  }

  clear() {
    this.queue = [];
    if (this.audioContext) {
      this.nextTime = this.audioContext.currentTime;
    }
  }

  destroy() {
    this.clear();
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }
  }
}

// Screen recording utility functions [web:62][web:65]
class ScreenRecorder {
  constructor() {
    this.mediaRecorder = null;
    this.recordedChunks = [];
    this.stream = null;
    this.isRecording = false;
  }

  async startRecording() {
    try {
      console.log("🎬 Starting enhanced screen recording...");

      // Request screen capture with audio [web:62][web:65]
      this.stream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          mediaSource: "screen",
          width: { ideal: 1920, max: 1920 },
          height: { ideal: 1080, max: 1080 },
          frameRate: { ideal: 30, max: 60 },
        },
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100,
        },
      });

      // Add microphone audio to the recording [web:47][web:65]
      try {
        const audioStream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            sampleRate: 44100,
            autoGainControl: true,
          },
        });

        const audioTrack = audioStream.getTracks()[0];
        if (audioTrack) {
          this.stream.addTrack(audioTrack);
          console.log("🎤 Added microphone audio to screen recording");
        }
      } catch (audioError) {
        console.warn("⚠️ Could not add microphone audio:", audioError.message);
      }

      // Configure MediaRecorder with best available format [web:42][web:73]
      let mimeType = "video/webm; codecs=vp9,opus";
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = "video/webm; codecs=vp8,opus";
        if (!MediaRecorder.isTypeSupported(mimeType)) {
          mimeType = "video/webm";
          if (!MediaRecorder.isTypeSupported(mimeType)) {
            mimeType = "video/mp4";
          }
        }
      }

      console.log("📹 Using MIME type:", mimeType);

      this.mediaRecorder = new MediaRecorder(this.stream, {
        mimeType: mimeType,
        videoBitsPerSecond: 2500000, // 2.5 Mbps
        audioBitsPerSecond: 128000, // 128 kbps
      });

      this.recordedChunks = [];

      // Handle recording events [web:73][web:62]
      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          this.recordedChunks.push(event.data);
          console.log(`📊 Recording chunk: ${event.data.size} bytes`);
        }
      };

      this.mediaRecorder.onstop = () => {
        console.log(
          "🛑 Screen recording stopped, total chunks:",
          this.recordedChunks.length
        );
        this.isRecording = false;
      };

      this.mediaRecorder.onerror = (event) => {
        console.error("❌ MediaRecorder error:", event.error);
      };

      // Handle screen sharing end [web:65]
      this.stream.getVideoTracks()[0].addEventListener("ended", () => {
        console.log("📺 Screen sharing ended by user");
        this.stopRecording();
      });

      // Start recording [web:62][web:65]
      this.mediaRecorder.start(1000); // Collect data every second
      this.isRecording = true;

      console.log("✅ Screen recording started successfully");
      return true;
    } catch (error) {
      console.error("❌ Error starting screen recording:", error);
      throw new Error(`Failed to start screen recording: ${error.message}`);
    }
  }

  stopRecording() {
    return new Promise((resolve) => {
      if (this.mediaRecorder && this.mediaRecorder.state !== "inactive") {
        this.mediaRecorder.addEventListener("stop", resolve, { once: true });
        this.mediaRecorder.stop();
      } else {
        resolve();
      }

      if (this.stream) {
        this.stream.getTracks().forEach((track) => {
          track.stop();
          console.log("🔇 Stopped track:", track.kind);
        });
        this.stream = null;
      }

      this.isRecording = false;
    });
  }

  async getRecordingBlob() {
    if (this.recordedChunks.length === 0) {
      console.warn("⚠️ No recording chunks available");
      return null;
    }

    const blob = new Blob(this.recordedChunks, {
      type: this.mediaRecorder?.mimeType || "video/webm",
    });

    console.log("📦 Created recording blob:", blob.size, "bytes");
    return blob;
  }

  async getRecordingBase64() {
    const blob = await this.getRecordingBlob();
    if (!blob) return null;

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result;
        if (typeof result === "string") {
          console.log("📝 Converted to base64:", result.length, "characters");
          resolve(result);
        } else {
          reject(new Error("FileReader result is not a string"));
        }
      };
      reader.onerror = () => reject(new Error("FileReader error"));
      reader.readAsDataURL(blob);
    });
  }
}

// Proctoring system [web:64][web:69][web:75]
class ProctoringSystem {
  constructor() {
    this.violations = [];
    this.screenshots = [];
    this.tabSwitchCount = 0;
    this.isActive = false;
    this.screenshotInterval = null;
    this.onViolation = null;
    this.onScreenshot = null;
  }

  start(webcamRef, onViolation, onScreenshot) {
    this.isActive = true;
    this.onViolation = onViolation;
    this.onScreenshot = onScreenshot;
    console.log("🔒 Proctoring system activated");

    // Tab switching detection [web:64][web:69]
    this.setupTabSwitchDetection();

    // Keyboard shortcuts detection [web:64]
    this.setupKeyboardMonitoring();

    // Context menu blocking [web:75]
    this.setupContextMenuBlocking();

    // Periodic screenshots [web:69][web:72]
    this.setupPeriodicScreenshots(webcamRef);
  }

  setupTabSwitchDetection() {
    const handleVisibilityChange = () => {
      if (document.hidden && this.isActive) {
        this.tabSwitchCount++;
        const violation = {
          type: "TAB_SWITCH",
          timestamp: new Date().toISOString(),
          description: "Candidate switched tabs or minimized window",
          count: this.tabSwitchCount,
        };
        this.logViolation(violation);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    this.cleanup.push(() => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    });
  }

  setupKeyboardMonitoring() {
    const handleKeyDown = (e) => {
      if (!this.isActive) return;

      const forbiddenKeys = [
        { key: "F12", description: "Developer tools shortcut" },
        {
          combo: e.ctrlKey && e.shiftKey && e.key === "I",
          description: "Developer tools (Ctrl+Shift+I)",
        },
        {
          combo: e.ctrlKey && e.shiftKey && e.key === "J",
          description: "Console (Ctrl+Shift+J)",
        },
        {
          combo: e.ctrlKey && e.key === "u",
          description: "View source (Ctrl+U)",
        },
        {
          combo: e.altKey && e.key === "Tab",
          description: "Alt+Tab application switching",
        },
        {
          combo: e.ctrlKey && e.key === "r",
          description: "Page refresh attempt",
        },
        { combo: e.key === "F5", description: "Page refresh attempt" },
      ];

      for (const forbidden of forbiddenKeys) {
        if (forbidden.key === e.key || forbidden.combo) {
          e.preventDefault();
          const violation = {
            type: "FORBIDDEN_KEY",
            timestamp: new Date().toISOString(),
            description: `Attempted forbidden action: ${forbidden.description}`,
            key: e.key,
            modifiers: {
              ctrl: e.ctrlKey,
              shift: e.shiftKey,
              alt: e.altKey,
            },
          };
          this.logViolation(violation);
          break;
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    this.cleanup.push(() => {
      document.removeEventListener("keydown", handleKeyDown);
    });
  }

  setupContextMenuBlocking() {
    const handleContextMenu = (e) => {
      if (this.isActive) {
        e.preventDefault();
        const violation = {
          type: "RIGHT_CLICK",
          timestamp: new Date().toISOString(),
          description: "Attempted to open context menu",
        };
        this.logViolation(violation);
      }
    };

    document.addEventListener("contextmenu", handleContextMenu);
    this.cleanup.push(() => {
      document.removeEventListener("contextmenu", handleContextMenu);
    });
  }

  setupPeriodicScreenshots(webcamRef) {
    this.screenshotInterval = setInterval(() => {
      this.captureScreenshot(webcamRef);
    }, 30000); // Every 30 seconds
  }

  captureScreenshot(webcamRef) {
    if (webcamRef?.current) {
      try {
        const imageSrc = webcamRef.current.getScreenshot({
          width: 640,
          height: 480,
        });

        if (imageSrc) {
          const screenshot = {
            timestamp: new Date().toISOString(),
            image: imageSrc,
            quality: 0.8,
          };

          this.screenshots.push(screenshot);
          console.log(`📸 Screenshot captured (${this.screenshots.length})`);

          if (this.onScreenshot) {
            this.onScreenshot(screenshot);
          }
        }
      } catch (error) {
        console.error("❌ Screenshot capture error:", error);
      }
    }
  }

  logViolation(violation) {
    this.violations.push(violation);
    console.warn(
      "🚨 Proctoring violation:",
      violation.type,
      "-",
      violation.description
    );

    if (this.onViolation) {
      this.onViolation(violation);
    }
  }

  cleanup = [];

  stop() {
    this.isActive = false;

    if (this.screenshotInterval) {
      clearInterval(this.screenshotInterval);
      this.screenshotInterval = null;
    }

    // Run all cleanup functions
    this.cleanup.forEach((cleanupFn) => cleanupFn());
    this.cleanup = [];

    console.log("🔓 Proctoring system deactivated");
  }

  getReport() {
    return {
      violations: this.violations,
      screenshots: this.screenshots,
      tabSwitchCount: this.tabSwitchCount,
      summary: {
        totalViolations: this.violations.length,
        violationTypes: this.violations.reduce((acc, v) => {
          acc[v.type] = (acc[v.type] || 0) + 1;
          return acc;
        }, {}),
        screenshotCount: this.screenshots.length,
      },
    };
  }
}

// Main Interview Component [web:67][web:68][web:74]
export default function EnhancedInterviewSystem() {
  const { candidateId } = useParams();
  const navigate = useNavigate();

  // Core states
  const [isInterviewActive, setIsInterviewActive] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [status, setStatus] = useState("Ready to start");
  const [error, setError] = useState(null);
  const [sessionId, setSessionId] = useState(null);

  // Candidate data
  const [candidate, setCandidate] = useState(null);
  const [loading, setLoading] = useState(true);

  // Audio states
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState("");

  // Media controls
  const [isCameraOn, setIsCameraOn] = useState(true);
  const [isMicOn, setIsMicOn] = useState(true);
  const [showControls, setShowControls] = useState(true);
  const [showInstructions, setShowInstructions] = useState(true);

  // Recording states
  const [isRecording, setIsRecording] = useState(false);
  const [recordingStatus, setRecordingStatus] = useState("Not Recording");
  const [screenRecordingData, setScreenRecordingData] = useState(null);

  // Proctoring states
  const [violations, setViolations] = useState([]);
  const [screenshots, setScreenshots] = useState([]);
  const [tabSwitchCount, setTabSwitchCount] = useState(0);

  // Refs for system components
  const webcamRef = useRef(null);
  const wsRef = useRef(null);
  const playerRef = useRef(new AudioPlayer());
  const screenRecorderRef = useRef(new ScreenRecorder());
  const proctoringRef = useRef(new ProctoringSystem());
  const controlsTimeoutRef = useRef(null);

  // Microphone management
  const micRef = useRef({
    stream: null,
    audioContext: null,
    processor: null,
    source: null,
    running: false,
  });

  // Webcam configuration [web:67]
  const videoConstraints = {
    width: { ideal: 1280, max: 1920 },
    height: { ideal: 720, max: 1080 },
    facingMode: "user",
    frameRate: { ideal: 30, max: 60 },
  };

  // Fetch candidate data on mount
  useEffect(() => {
    const fetchCandidate = async () => {
      if (!candidateId) {
        setError("No candidate ID provided");
        setLoading(false);
        return;
      }

      try {
        console.log("👤 Fetching candidate:", candidateId);
        const response = await fetch(
          `https://prod.toxboxai.com/api/candidates/${candidateId}`
        );

        if (!response.ok) {
          throw new Error(`Candidate not found: ${response.status}`);
        }

        const candidateData = await response.json();
        setCandidate(candidateData);
        setLoading(false);
        console.log("✅ Candidate loaded:", candidateData.name);
      } catch (error) {
        console.error("❌ Failed to fetch candidate:", error);
        setError(error.message);
        setLoading(false);
      }
    };

    fetchCandidate();
  }, [candidateId]);

  // Controls auto-hide functionality
  useEffect(() => {
    const handleMouseMove = () => {
      setShowControls(true);
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
      controlsTimeoutRef.current = setTimeout(() => {
        if (isInterviewActive) {
          setShowControls(false);
        }
      }, 3000);
    };

    if (isInterviewActive) {
      document.addEventListener("mousemove", handleMouseMove);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    };
  }, [isInterviewActive]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (playerRef.current) {
        playerRef.current.destroy();
      }
      if (proctoringRef.current) {
        proctoringRef.current.stop();
      }
      if (screenRecorderRef.current) {
        screenRecorderRef.current.stopRecording();
      }
    };
  }, []);

  // Screen recording functions [web:62][web:65]
  const startScreenRecording = async () => {
    try {
      setRecordingStatus("Starting...");
      await screenRecorderRef.current.startRecording();
      setIsRecording(true);
      setRecordingStatus("Recording Active");
      console.log("✅ Screen recording started");
    } catch (error) {
      console.error("❌ Screen recording failed:", error);
      setError(`Screen recording failed: ${error.message}`);
      setRecordingStatus("Recording Failed");
    }
  };

  const stopScreenRecording = async () => {
    try {
      setRecordingStatus("Stopping...");
      await screenRecorderRef.current.stopRecording();

      const base64Data = await screenRecorderRef.current.getRecordingBase64();
      if (base64Data) {
        setScreenRecordingData(base64Data);
        console.log("✅ Screen recording stopped and converted");
      }

      setIsRecording(false);
      setRecordingStatus("Recording Stopped");
    } catch (error) {
      console.error("❌ Error stopping screen recording:", error);
      setRecordingStatus("Stop Failed");
    }
  };

  // Microphone management [web:66][web:74]
  const startMicrophone = async () => {
    try {
      console.log("🎤 Starting microphone...");

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          sampleRate: SAMPLE_RATE,
          echoCancellation: true,
          noiseSuppression: false,
          autoGainControl: true,
        },
      });

      const audioContext = new (window.AudioContext ||
        window.webkitAudioContext)();
      await audioContext.resume();

      const source = audioContext.createMediaStreamSource(stream);
      const processor = audioContext.createScriptProcessor(4096, 1, 1);

      let chunkBuffers = [];
      const chunkSamplesTarget = Math.round(
        (CHUNK_MS / 1000) * audioContext.sampleRate
      );
      let lastSpeechTime = 0;
      let lastCommitTime = 0;

      processor.onaudioprocess = (event) => {
        if (!micRef.current.running) return;

        const inputData = event.inputBuffer.getChannelData(0);
        const rms = Math.sqrt(
          inputData.reduce((sum, sample) => sum + sample * sample, 0) /
          inputData.length
        );

        setAudioLevel(rms);
        const isSpeechDetected = rms > AUDIO_THRESHOLD;

        if (isSpeechDetected) {
          lastSpeechTime = Date.now();
          if (!isSpeaking) {
            setIsSpeaking(true);
            setIsListening(false);
          }
        }

        // Buffer audio data
        const copy = new Float32Array(inputData.length);
        copy.set(inputData);
        chunkBuffers.push(copy);

        const totalSamples = chunkBuffers.reduce(
          (sum, buffer) => sum + buffer.length,
          0
        );

        if (totalSamples >= chunkSamplesTarget) {
          const merged = new Float32Array(totalSamples);
          let offset = 0;

          for (const buffer of chunkBuffers) {
            merged.set(buffer, offset);
            offset += buffer.length;
          }

          chunkBuffers = [];
          const pcmBuffer = floatTo16BitPCM(merged);

          // Send audio to WebSocket
          if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
            wsRef.current.send(pcmBuffer);
          }

          // Handle silence detection
          const now = Date.now();
          const timeSinceLastSpeech = now - lastSpeechTime;

          if (
            isSpeaking &&
            timeSinceLastSpeech > SILENCE_DURATION &&
            now - lastCommitTime > 500
          ) {
            lastCommitTime = now;
            setIsSpeaking(false);
            setIsListening(true);

            if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
              wsRef.current.send(JSON.stringify({ type: "commit" }));
            }
          }
        }
      };

      source.connect(processor);
      processor.connect(audioContext.destination);

      micRef.current = {
        stream,
        audioContext,
        processor,
        source,
        running: true,
      };

      console.log("✅ Microphone started successfully");
    } catch (error) {
      console.error("❌ Microphone failed:", error);
      setError("Microphone access denied");
      throw error;
    }
  };

  const stopMicrophone = () => {
    if (micRef.current.running) {
      const { stream, audioContext, processor, source } = micRef.current;

      try {
        if (processor) processor.disconnect();
        if (source) source.disconnect();
        if (stream) stream.getTracks().forEach((track) => track.stop());
        if (audioContext && audioContext.state !== "closed") {
          audioContext.close();
        }
      } catch (error) {
        console.warn("⚠️ Error stopping microphone:", error);
      }

      micRef.current.running = false;
      console.log("🔇 Microphone stopped");
    }
  };

  // WebSocket management [web:68][web:74]
  const connectWebSocket = () => {
    return new Promise((resolve, reject) => {
      console.log("🔌 Connecting to interview WebSocket...");

      const ws = new WebSocket("ws://localhost:8082");
      ws.binaryType = "arraybuffer";

      const connectionTimeout = setTimeout(() => {
        console.error("❌ WebSocket connection timeout");
        ws.close();
        reject(new Error("WebSocket connection timeout"));
      }, 10000);

      ws.onopen = () => {
        clearTimeout(connectionTimeout);
        console.log("✅ WebSocket connected successfully");
        setIsConnected(true);
        resolve(ws);
      };

      ws.onmessage = async (event) => {
        try {
          if (typeof event.data === "string") {
            const data = JSON.parse(event.data);

            // Handle different message types
            if (data.source === "ultravox") {
              if (data.event?.type === "transcript") {
                if (data.event.speaker === "agent") {
                  setCurrentQuestion(data.event.text);
                  setIsListening(true);
                  setIsSpeaking(false);
                }
              }
            } else if (data.source === "server") {
              if (data.event === "ultravox_connected") {
                setStatus("AI Interviewer Connected");
                setCurrentQuestion(
                  `Hello ${candidate?.name}! I'm Anjali, your JavaScript interviewer. Let's begin!`
                );
              }
            }
          } else if (event.data instanceof ArrayBuffer) {
            // Handle binary audio data
            if (event.data.byteLength > 0) {
              await playerRef.current.playAudio(event.data);
            }
          }
        } catch (error) {
          console.error("❌ WebSocket message error:", error);
        }
      };

      ws.onclose = (event) => {
        clearTimeout(connectionTimeout);
        console.log("❌ WebSocket closed:", event.code, event.reason);
        setIsConnected(false);
        setStatus("Connection Lost");
      };

      ws.onerror = (error) => {
        clearTimeout(connectionTimeout);
        console.error("❌ WebSocket error:", error);
        reject(error);
      };

      wsRef.current = ws;
    });
  };

  // Backend API calls
  const sendViolationToBackend = async (violation) => {
    try {
      await fetch(
        "https://prod.toxboxai.com/api/interview/violation",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            candidateId,
            violation,
          }),
        }
      );
    } catch (error) {
      console.error("❌ Failed to log violation:", error);
    }
  };

  const sendScreenshotToBackend = async (screenshot) => {
    try {
      await fetch(
        "https://prod.toxboxai.com/api/interview/screenshot",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            candidateId,
            screenshot,
          }),
        }
      );
    } catch (error) {
      console.error("❌ Failed to send screenshot:", error);
    }
  };

  // Main interview control functions
  const startInterview = async () => {
    try {
      setError(null);
      setStatus("Starting interview...");
      setShowInstructions(false);

      console.log("🚀 Starting interview for candidate:", candidateId);

      // Enter fullscreen
      try {
        if (document.documentElement.requestFullscreen) {
          await document.documentElement.requestFullscreen();
        }
      } catch (fullscreenError) {
        console.warn("⚠️ Fullscreen failed:", fullscreenError);
      }

      // Start backend interview session
      const response = await fetch(
        "https://prod.toxboxai.com/api/interview/start",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ candidateId }),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to start interview: ${response.status}`);
      }

      const data = await response.json();
      setSessionId(data.sessionId);
      console.log("📋 Interview session started:", data.sessionId);

      // Start screen recording
      await startScreenRecording();

      // Connect WebSocket
      await connectWebSocket();

      // Start microphone
      await startMicrophone();

      // Start proctoring system
      proctoringRef.current.start(
        webcamRef,
        (violation) => {
          setViolations((prev) => [...prev, violation]);
          setTabSwitchCount((prev) =>
            violation.type === "TAB_SWITCH" ? prev + 1 : prev
          );
          sendViolationToBackend(violation);
        },
        (screenshot) => {
          setScreenshots((prev) => [...prev, screenshot]);
          sendScreenshotToBackend(screenshot);
        }
      );

      setIsInterviewActive(true);
      setStatus("Interview Active");
      console.log("✅ Interview started successfully");
    } catch (error) {
      console.error("❌ Failed to start interview:", error);
      setError(error.message);
      setStatus("Failed to Start");
    }
  };

  const endInterview = async () => {
    try {
      console.log("🏁 Ending interview...");
      setStatus("Ending interview...");

      // Stop screen recording and wait for processing
      if (isRecording) {
        await stopScreenRecording();

        // Wait for screen recording processing
        let waitCount = 0;
        while (!screenRecordingData && waitCount < 10) {
          console.log(
            `⏳ Waiting for recording processing... (${waitCount + 1}/10)`
          );
          await new Promise((resolve) => setTimeout(resolve, 500));
          waitCount++;
        }
      }

      // Stop all systems
      stopMicrophone();
      proctoringRef.current.stop();

      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }

      // Exit fullscreen
      try {
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        }
      } catch (fullscreenError) {
        console.warn("⚠️ Exit fullscreen failed:", fullscreenError);
      }

      // Get proctoring report
      const proctoringReport = proctoringRef.current.getReport();

      console.log("📤 Sending interview data to backend...");
      console.log("📹 Screen recording available:", !!screenRecordingData);
      console.log("📸 Screenshots count:", proctoringReport.screenshots.length);
      console.log("⚠️ Violations count:", proctoringReport.violations.length);

      // Send final data to backend
      const requestBody = {
        candidateId,
        violations: proctoringReport.violations,
        screenshots: proctoringReport.screenshots,
        tabSwitchCount: proctoringReport.tabSwitchCount,
        screenRecording: screenRecordingData,
      };

      const response = await fetch(
        "https://prod.toxboxai.com/api/interview/end",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestBody),
        }
      );

      const result = await response.json();
      console.log("✅ Interview ended successfully:", result);

      // Update states
      setIsInterviewActive(false);
      setIsConnected(false);
      setIsSpeaking(false);
      setIsListening(false);
      setStatus("Interview Completed");

      // Navigate to completion page
      setTimeout(() => {
        navigate("/interview/complete", {
          state: {
            candidateId,
            sessionId,
            violations: proctoringReport.violations,
            tabSwitchCount: proctoringReport.tabSwitchCount,
            screenshotCount: proctoringReport.screenshots.length,
            screenRecordingUploaded: result.screenRecordingUploaded || false,
          },
        });
      }, 2000);
    } catch (error) {
      console.error("❌ Error ending interview:", error);
      setError(`Failed to end interview: ${error.message}`);
    }
  };

  // Toggle functions
  const toggleMicrophone = () => {
    setIsMicOn((prev) => {
      const newState = !prev;
      if (micRef.current.running) {
        micRef.current.stream?.getAudioTracks().forEach((track) => {
          track.enabled = newState;
        });
      }
      return newState;
    });
  };

  const toggleCamera = () => {
    setIsCameraOn((prev) => !prev);
  };

  // Helper functions
  const handleViolation = useCallback((violation) => {
    setViolations((prev) => [...prev, violation]);
  }, []);

  const handleScreenshot = useCallback((screenshot) => {
    setScreenshots((prev) => [...prev, screenshot]);
  }, []);

  // Loading screen
  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingCard}>
          <div className={styles.loadingSpinner}>
            <MdRefresh className={styles.spinnerIcon} />
          </div>
          <h2 className={styles.loadingTitle}>Initializing Interview</h2>
          <p className={styles.loadingSubtitle}>
            Loading enhanced interview platform...
          </p>
          <div className={styles.loadingBar}>
            <div className={styles.loadingProgress}></div>
          </div>
        </div>
      </div>
    );
  }

  // Error screen
  if (!candidate) {
    return (
      <div className={styles.errorContainer}>
        <div className={styles.errorCard}>
          <MdErrorOutline className={styles.errorIcon} />
          <h2 className={styles.errorTitle}>Interview Not Found</h2>
          <p className={styles.errorMessage}>
            The candidate information could not be found. Please check the
            interview link.
          </p>
          <button
            className={`${styles.btn} ${styles.btnPrimary}`}
            onClick={() => navigate("/")}
          >
            <MdArrowBack />
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Instructions screen
  if (showInstructions && !isInterviewActive) {
    return (
      <div className={styles.instructionsContainer}>
        <div className={styles.instructionsWrapper}>
          {/* Header */}
          <header className={styles.header}>
            <div className={styles.headerContent}>
              <div className={styles.logo}>
                <MdPlayArrow className={styles.logoIcon} />
                <span>InterviewAI</span>
              </div>
              <div className={styles.headerActions}>
                <button
                  className={styles.btnSecondary}
                  onClick={() => navigate("/")}
                >
                  <MdClose />
                </button>
              </div>
            </div>
          </header>

          <div className={styles.mainContent}>
            {/* Hero Section */}
            <section className={styles.hero}>
              <div className={styles.heroContent}>
                <div className={styles.heroInfo}>
                  <h1 className={styles.heroTitle}>
                    <MdPersonOutline className={styles.heroIcon} />
                    Welcome, {candidate.name}
                  </h1>
                  <p className={styles.heroSubtitle}>
                    Get ready for your JavaScript technical interview with our
                    AI-powered platform
                  </p>
                  <div className={styles.candidateCard}>
                    <div className={styles.candidateAvatar}>
                      <MdPersonOutline />
                    </div>
                    <div className={styles.candidateDetails}>
                      <h3>{candidate.name}</h3>
                      <p>
                        {candidate.position} • {candidate.experience} years
                        experience
                      </p>
                      <p className={styles.candidateEmail}>{candidate.email}</p>
                    </div>
                  </div>
                </div>
                <div className={styles.heroStats}>
                  <div className={styles.statCard}>
                    <MdTimer />
                    <div>
                      <div className={styles.statNumber}>30-45</div>
                      <div className={styles.statLabel}>Minutes</div>
                    </div>
                  </div>
                  <div className={styles.statCard}>
                    <MdLockOutline />
                    <div>
                      <div className={styles.statNumber}>100%</div>
                      <div className={styles.statLabel}>Secure</div>
                    </div>
                  </div>
                  <div className={styles.statCard}>
                    <MdCloudUpload />
                    <div>
                      <div className={styles.statNumber}>AWS S3</div>
                      <div className={styles.statLabel}>Storage</div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Features Section */}
            <section className={styles.featuresSection}>
              <h2 className={styles.sectionTitle}>
                <MdCheckCircleOutline />
                What to Expect
              </h2>
              <div className={styles.featuresGrid}>
                <div className={styles.featureCard}>
                  <div className={styles.featureIconWrapper}>
                    <MdScreenShare />
                  </div>
                  <h3>Full Screen Recording</h3>
                  <p>
                    Your entire screen will be recorded with audio for
                    comprehensive review
                  </p>
                </div>
                <div className={styles.featureCard}>
                  <div className={styles.featureIconWrapper}>
                    <MdMic />
                  </div>
                  <h3>AI Conversation</h3>
                  <p>
                    Interactive interview with Anjali, our AI JavaScript
                    specialist
                  </p>
                </div>
                <div className={styles.featureCard}>
                  <div className={styles.featureIconWrapper}>
                    <MdVisibility />
                  </div>
                  <h3>Proctoring System</h3>
                  <p>
                    Advanced monitoring ensures a fair and focused interview
                    environment
                  </p>
                </div>
              </div>
            </section>

            {/* Guidelines Section */}
            <section className={styles.guidelinesSection}>
              <h2 className={styles.sectionTitle}>
                <MdInfoOutline />
                Interview Guidelines
              </h2>
              <div className={styles.guidelinesGrid}>
                <div className={styles.guidelineCard}>
                  <MdWarningAmber className={styles.guidelineIcon} />
                  <div>
                    <h3>Stay Focused</h3>
                    <p>
                      Keep your attention on the interview. Tab switching will
                      be detected
                    </p>
                  </div>
                </div>
                <div className={styles.guidelineCard}>
                  <MdWarningAmber className={styles.guidelineIcon} />
                  <div>
                    <h3>No External Help</h3>
                    <p>
                      Avoid using external resources, copy/paste, or developer
                      tools
                    </p>
                  </div>
                </div>
                <div className={styles.guidelineCard}>
                  <MdWarningAmber className={styles.guidelineIcon} />
                  <div>
                    <h3>Camera Required</h3>
                    <p>
                      Keep your camera on and ensure good lighting throughout
                      the interview
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Monitoring Section */}
            <section className={styles.monitoringSection}>
              <h2 className={styles.sectionTitle}>
                <MdLockOutline />
                Active Monitoring
              </h2>
              <div className={styles.monitoringFeatures}>
                <div className={styles.monitoringFeature}>
                  <MdScreenShare />
                  <span>Screen + Audio Recording</span>
                </div>
                <div className={styles.monitoringFeature}>
                  <MdCameraAlt />
                  <span>Webcam Snapshots</span>
                </div>
                <div className={styles.monitoringFeature}>
                  <MdKeyboard />
                  <span>Keyboard Activity</span>
                </div>
                <div className={styles.monitoringFeature}>
                  <MdMouse />
                  <span>Mouse Tracking</span>
                </div>
                <div className={styles.monitoringFeature}>
                  <MdVisibility />
                  <span>Focus Detection</span>
                </div>
                <div className={styles.monitoringFeature}>
                  <MdCloudUpload />
                  <span>Secure Cloud Storage</span>
                </div>
              </div>
            </section>

            {/* CTA Section */}
            <section className={styles.ctaSection}>
              {error && (
                <div className={styles.errorBanner}>
                  <MdErrorOutline />
                  <span>{error}</span>
                </div>
              )}

              <div className={styles.ctaContent}>
                <div className={styles.consentText}>
                  <MdInfoOutline />
                  <p>
                    By starting this interview, you consent to our{" "}
                    <strong>recording policy</strong> and{" "}
                    <strong>monitoring guidelines</strong>. All data is securely
                    stored and used only for evaluation purposes.
                  </p>
                </div>
                <button
                  className={`${styles.btn} ${styles.btnPrimary} ${styles.startButton}`}
                  onClick={startInterview}
                  disabled={loading}
                >
                  <MdPlayArrow />
                  <span>Begin Interview</span>
                  <MdKeyboardArrowRight />
                </button>
                <div className={styles.ctaFooter}>
                  <MdTimer />
                  <span>Estimated time: 30-45 minutes</span>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    );
  }

  // Main interview interface
  return (
    <div className={styles.interviewContainer}>
      {/* Top Bar */}
      <div className={styles.topBar}>
        <div className={styles.topBarContent}>
          <div className={styles.candidateInfo}>
            <div className={styles.avatar}>
              <MdPersonOutline />
            </div>
            <div>
              <h2>{candidate.name}</h2>
              <div className={styles.sessionId}>
                <MdInfoOutline />
                Session: {sessionId?.slice(0, 8) || "Not started"}
              </div>
            </div>
          </div>

          <div className={styles.connectionStatus}>
            <div
              className={`${styles.statusIndicator} ${isConnected ? styles.connected : styles.disconnected
                }`}
            >
              <div className={styles.statusDot}></div>
              {isConnected ? "Live" : "Offline"}
            </div>
          </div>
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div className={styles.errorBanner}>
          <MdErrorOutline />
          <span>{error}</span>
          <button onClick={() => setError(null)} className={styles.closeButton}>
            <MdClose />
          </button>
        </div>
      )}

      <div className={styles.mainLayout}>
        {/* Video Section */}
        <section className={styles.videoSection}>
          <div className={styles.videoContainer}>
            {isCameraOn ? (
              <Webcam
                audio={isMicOn}
                ref={webcamRef}
                screenshotFormat="image/jpeg"
                videoConstraints={videoConstraints}
                className={styles.webcam}
              />
            ) : (
              <div className={styles.cameraOff}>
                <MdVideocamOff className={styles.cameraOffIcon} />
                <div className={styles.cameraOffText}>Camera Disabled</div>
              </div>
            )}

            {/* Video Overlay */}
            <div className={styles.videoOverlay}>
              {/* Top Controls */}
              <div className={styles.topControls}>
                <button
                  className={`${styles.controlBtn} ${!isMicOn ? styles.active : ""
                    }`}
                  onClick={toggleMicrophone}
                  title="Toggle Microphone"
                >
                  {isMicOn ? <MdMic /> : <MdMicOff />}
                </button>

                <button
                  className={`${styles.controlBtn} ${!isCameraOn ? styles.active : ""
                    }`}
                  onClick={toggleCamera}
                  title="Toggle Camera"
                >
                  {isCameraOn ? <MdVideocam /> : <MdVideocamOff />}
                </button>

                {/* Audio Level */}
                <div className={styles.audioMeter}>
                  <div
                    className={styles.audioBar}
                    style={{
                      height: `${Math.min(
                        (audioLevel / AUDIO_THRESHOLD) * 100,
                        100
                      )}%`,
                    }}
                  />
                </div>
              </div>

              {/* Status Indicators */}
              <div className={styles.statusPanel}>
                <div
                  className={`${styles.statusItem} ${isSpeaking ? styles.active : ""
                    }`}
                >
                  <MdMic />
                  <span>{isSpeaking ? "Speaking" : "Silent"}</span>
                </div>
                <div
                  className={`${styles.statusItem} ${isListening ? styles.active : ""
                    }`}
                >
                  <MdVolumeUp />
                  <span>{isListening ? "Listening" : "Waiting"}</span>
                </div>
              </div>

              {/* Recording Indicator */}
              {isRecording && (
                <div className={styles.recordingIndicator}>
                  <div className={styles.recordingDot}></div>
                  <span>Recording</span>
                </div>
              )}
            </div>

            {/* Bottom Info */}
            <div className={styles.videoInfo}>
              <div className={styles.userLabel}>
                <MdPersonOutline />
                {candidate.name}
              </div>

              {tabSwitchCount > 0 && (
                <div className={styles.violationBadge}>
                  <MdWarningAmber />
                  <span>{tabSwitchCount} tab switches</span>
                </div>
              )}
            </div>
          </div>

          {/* Question Display */}
          {currentQuestion && (
            <div className={styles.questionCard}>
              <div className={styles.questionHeader}>
                <MdInfoOutline />
                Interviewer Question
              </div>
              <div className={styles.questionContent}>{currentQuestion}</div>
            </div>
          )}
        </section>

        {/* Control Panel */}
        <aside className={styles.controlPanel}>
          {/* AI Interviewer */}
          <div className={styles.aiCard}>
            <div className={styles.aiHeader}>
              <div className={styles.aiAvatar}>
                <MdPersonOutline />
              </div>
              <div>
                <h3>Anjali</h3>
                <p className={styles.aiRole}>JavaScript Interviewer</p>
              </div>
            </div>

            <div className={styles.aiStatus}>
              <div
                className={`${styles.statusBubble} ${isListening ? styles.listening : ""
                  }`}
              >
                <MdMic />
                {isListening
                  ? "Listening"
                  : isSpeaking
                    ? "Processing"
                    : "Ready"}
              </div>
            </div>

            {/* Quick Stats */}
            <div className={styles.statsGrid}>
              <div className={styles.statItem}>
                <div className={styles.statValue}>{screenshots.length}</div>
                <div className={styles.statLabel}>Snapshots</div>
              </div>
              <div className={styles.statItem}>
                <div
                  className={`${styles.statValue} ${violations.length > 0 ? styles.warning : ""
                    }`}
                >
                  {violations.length}
                </div>
                <div className={styles.statLabel}>Alerts</div>
              </div>
              <div className={styles.statItem}>
                <div className={styles.statValue}>
                  {isConnected ? "●" : "○"}
                </div>
                <div className={styles.statLabel}>Connection</div>
              </div>
            </div>

            {/* Main Control Button */}
            <button
              className={`${styles.btn} ${styles.btnDestructive} ${styles.endButton}`}
              onClick={endInterview}
              disabled={!isInterviewActive}
            >
              <MdStop />
              End Interview
            </button>
          </div>

          {/* Monitoring Panel */}
          <div className={styles.monitoringCard}>
            <h3 className={styles.cardTitle}>
              <MdLockOutline />
              Monitoring Status
            </h3>

            <div className={styles.monitoringGrid}>
              <div className={styles.monitoringItem}>
                <MdScreenShare
                  className={`${styles.monitoringIcon} ${isRecording ? styles.active : ""
                    }`}
                />
                <span>Screen Recording</span>
                <div
                  className={styles.statusDot}
                  style={{ opacity: isRecording ? 1 : 0.3 }}
                ></div>
              </div>

              <div className={styles.monitoringItem}>
                <MdCameraAlt className={styles.monitoringIcon} />
                <span>Webcam</span>
                <div
                  className={styles.statusDot}
                  style={{ opacity: isCameraOn ? 1 : 0.3 }}
                ></div>
              </div>

              <div className={styles.monitoringItem}>
                <MdMic
                  className={`${styles.monitoringIcon} ${isMicOn ? styles.active : ""
                    }`}
                />
                <span>Microphone</span>
                <div
                  className={styles.statusDot}
                  style={{ opacity: isMicOn ? 1 : 0.3 }}
                ></div>
              </div>

              <div className={styles.monitoringItem}>
                <MdCloudUpload className={styles.monitoringIcon} />
                <span>Cloud Storage</span>
                <div className={styles.statusDot}></div>
              </div>
            </div>
          </div>

          {/* System Info */}
          <div className={styles.systemInfo}>
            <div className={styles.infoItem}>
              <MdTimer />
              <span>{new Date().toLocaleTimeString()}</span>
            </div>
            <div className={styles.infoItem}>
              <MdInfoOutline />
              <span>Enhanced JS Interview</span>
            </div>
            <div className={styles.statusText}>{status}</div>
          </div>
        </aside>
      </div>

      {/* Floating Controls */}
      <div className={styles.floatingControls}>
        <button
          className={`${styles.floatingBtn} ${!isMicOn ? styles.active : ""}`}
          onClick={toggleMicrophone}
          title="Toggle Microphone"
        >
          {isMicOn ? <MdMic /> : <MdMicOff />}
        </button>

        <button
          className={`${styles.floatingBtn} ${!isCameraOn ? styles.active : ""
            }`}
          onClick={toggleCamera}
          title="Toggle Camera"
        >
          {isCameraOn ? <MdVideocam /> : <MdVideocamOff />}
        </button>

        <button
          className={`${styles.floatingBtn} ${styles.endBtn}`}
          onClick={endInterview}
          title="End Interview"
        >
          <MdStop />
        </button>
      </div>
    </div>
  );
}
